import React from 'react';

export function buildUiHelpers({ COLORS, th }) {
  function Card({ children, style }) {
    return (
      <div style={{
        background: COLORS.card,
        border: `1px solid ${COLORS.border}`,
        borderRadius: '16px',
        padding: '20px',
        boxShadow: COLORS.shadow,
        boxSizing: 'border-box',
        ...style,
      }}>
        {children}
      </div>
    );
  }

  function Badge({ color = 'indigo', children }) {
    const map = {
      green: { bg: '#14532d44', text: COLORS.green, border: `${COLORS.green}44` },
      red: { bg: '#7f1d1d44', text: COLORS.red, border: `${COLORS.red}44` },
      yellow: { bg: '#713f1244', text: COLORS.yellow, border: `${COLORS.yellow}44` },
      indigo: { bg: '#312e8144', text: COLORS.indigo, border: `${COLORS.indigo}44` },
      cyan: { bg: '#164e6344', text: COLORS.cyan, border: `${COLORS.cyan}44` },
      ghost: { bg: '#ffffff08', text: COLORS.muted, border: COLORS.border },
    };
    const c = map[color] || map.indigo;
    return (
      <span style={{
        background: c.bg,
        color: c.text,
        border: `1px solid ${c.border}`,
        borderRadius: '999px',
        padding: '2px 9px',
        fontSize: '11px',
        fontWeight: '700',
        display: 'inline-flex',
        alignItems: 'center',
        whiteSpace: 'nowrap',
      }}>
        {children}
      </span>
    );
  }

  function Btn({ children, onClick, color = 'accent', size = 'md', style, ...props }) {
    const colors = {
      accent: { bg: COLORS.accent, text: '#052728' },
      green: { bg: '#16a34a', text: '#fff' },
      red: { bg: '#dc2626', text: '#fff' },
      ghost: { bg: '#ffffff08', text: COLORS.text },
    };
    const c = colors[color] || colors.accent;
    const pad = size === 'sm' ? '6px 11px' : '9px 16px';
    return (
      <button
        onClick={onClick}
        style={{
          background: c.bg,
          color: c.text,
          border: 'none',
          borderRadius: '10px',
          padding: pad,
          fontSize: size === 'sm' ? '12px' : '13px',
          fontWeight: '700',
          cursor: 'pointer',
          transition: 'filter 120ms ease',
          boxShadow: color === 'ghost' ? 'none' : '0 4px 14px rgba(0,0,0,0.18)',
          ...style,
        }}
        {...props}
      >
        {children}
      </button>
    );
  }

  function SortHeader({ label, sortKey, activeSort, onToggle, align = 'left', style, title }) {
    const isActive = activeSort?.key === sortKey;
    const indicator = !isActive ? '↕' : activeSort.direction === 'asc' ? '↑' : '↓';
    return (
      <th style={{ ...th, ...style, textAlign: align }}>
        <button
          type="button"
          onClick={() => onToggle(sortKey)}
          title={title}
          style={{
            background: 'transparent',
            border: 'none',
            padding: 0,
            margin: 0,
            color: isActive ? COLORS.text : COLORS.muted,
            cursor: 'pointer',
            font: 'inherit',
            textTransform: 'inherit',
            letterSpacing: 'inherit',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <span>{label}</span>
          <span style={{ fontSize: '10px', color: isActive ? COLORS.cyan : COLORS.muted }}>{indicator}</span>
        </button>
      </th>
    );
  }

  return { Card, Badge, Btn, SortHeader };
}

export function getPositionSignalInfo(position) {
  if (position?.data_quality_warning && position?.is_index_member !== false) {
    return {
      signal: null,
      sigColor: "orange",
      rslOk: null,
      reason: position.data_quality_warning_reason,
    };
  }
  const backendSignal = position?.is_index_member === false
    ? 'austauschen'
    : String(position?.signal || '').trim().toLowerCase();
  if (['halten', 'beobachten', 'austauschen'].includes(backendSignal)) {
    const signal = backendSignal === 'halten'
      ? 'Halten'
      : backendSignal === 'beobachten'
        ? 'Beobachten'
        : 'Austauschen';
    const rslOk = (
      position?.rsl_titel !== null
      && position?.rsl_titel !== undefined
      && position?.rsl_index !== null
      && position?.rsl_index !== undefined
    )
      ? position.rsl_titel >= position.rsl_index
      : null;
    const sigColor = signal === 'Halten' ? 'green' : signal === 'Austauschen' ? 'red' : 'yellow';
    return { signal, sigColor, rslOk, maOk: position?.ma_ok, reason: position?.signal_reason };
  }
  const hasComparableRsl = (
    position?.rsl_titel !== null
    && position?.rsl_titel !== undefined
    && position?.rsl_index !== null
    && position?.rsl_index !== undefined
  );
  if (!hasComparableRsl) {
    return { signal: '-', sigColor: 'ghost', rslOk: null, maOk: position?.ma_ok };
  }
  const rslOk = position.rsl_titel >= position.rsl_index;
  const maOk = position?.ma_ok;
  const signal = position.rsl_titel < 1
    ? 'Austauschen'
    : rslOk && maOk
      ? 'Halten'
      : !rslOk
        ? 'Austauschen'
        : 'Beobachten';
  const sigColor = signal === 'Halten' ? 'green' : signal === 'Austauschen' ? 'red' : 'yellow';
  return { signal, sigColor, rslOk, maOk };
}

function compareSortValues(a, b) {
  const aMissing = a === null || a === undefined || a === '';
  const bMissing = b === null || b === undefined || b === '';
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b);
  return String(a).localeCompare(String(b), 'de', { numeric: true, sensitivity: 'base' });
}

export function sortBySpec(items, sortSpec, getValue) {
  const direction = sortSpec?.direction === 'desc' ? -1 : 1;
  const key = sortSpec?.key || 'rang';
  return [...items].sort((a, b) => {
    const primary = compareSortValues(getValue(a, key), getValue(b, key));
    if (primary !== 0) return primary * direction;
    const rankFallback = compareSortValues(getValue(a, 'rang'), getValue(b, 'rang'));
    if (rankFallback !== 0) return rankFallback;
    return compareSortValues(getValue(a, 'ticker'), getValue(b, 'ticker'));
  });
}

export function getDepotSectionIndexRsl(positionen) {
  const first = (positionen || []).find(item => Number.isFinite(item?.rsl_index));
  return first?.rsl_index ?? null;
}

export function getIndexBadgeColor(index) {
  if (index === 'HDAX') return 'cyan';
  if (index === 'NASDAQ100') return 'indigo';
  if (index === 'STOXX600') return 'green';
  if (index === 'CUSTOM') return 'yellow';
  return 'indigo';
}
